import React, { useEffect, useRef } from "react";
import { Chart } from "chart.js/auto";
import { modelAGF } from "../hooks/useModel";

const PaybackChart = ({
  square,
  rentalRate,
  loanAmount,
  interestRate,
  loanPeriod,
  checkbox,
  keyRate,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    const m = modelAGF(
      square,
      rentalRate,
      loanAmount,
      interestRate / 100,
      loanPeriod,
      checkbox,
      keyRate,
    );
    const years = [0, 1, 2, 3, 4, 5, 6, 7];
    const simplePayback = years.map((y) => m.ccfWithInvestYear?.[y] || 0);
    const discountedPayback = years.map((y) => m.dccf?.[y] || 0);

    if (chartInstance.current) chartInstance.current.destroy();

    chartInstance.current = new Chart(chartRef.current, {
      type: "line",
      data: {
        labels: years.map((y) => (y === 0 ? "инвест" : `${y}-й год`)),
        datasets: [
          {
            label: "Простой срок окупаемости",
            data: simplePayback,
            borderColor: "black",
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            pointRadius: 0,
          },
          {
            label: "Дисконтированный срок окупаемости",
            data: discountedPayback,
            borderColor: "black",
            borderWidth: 3,
            borderDash: [10, 5],
            fill: false,
            tension: 0.4,
            pointRadius: 0,
          },
          {
            label: "Ноль",
            data: new Array(8).fill(0),
            borderColor: "rgb(255, 99, 132)",
            borderWidth: 2,
            borderDash: [5, 5],
            fill: false,
            tension: 0,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          title: {
            display: true,
            text: "Расчетная окупаемость проекта",
            font: {
              family: "Montserrat, sans-serif",
              size: 16,
              weight: "bold",
            },
          },
          legend: {
            position: "bottom",
            labels: { font: { family: "Montserrat, sans-serif", size: 10 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                `${ctx.dataset.label}: ${Math.round(ctx.parsed.y).toLocaleString("ru-RU")}`,
            },
          },
        },
        scales: {
          y: {
            title: { display: true, text: "Накопленный денежный поток, руб" },
            ticks: {
              callback: (val) => val.toLocaleString("ru-RU"),
              color: (ctx) => (ctx.tick.value < 0 ? "red" : "black"),
            },
            afterBuildTicks: (scale) => {
              const ticks = scale.ticks;
              if (Math.min(...ticks.map((t) => t.value)) >= 0) {
                ticks.push({ value: -20000000 });
                ticks.sort((a, b) => a.value - b.value);
              }
              return ticks;
            },
          },
          x: { title: { display: true, text: "Период" } },
        },
      },
    });

    return () => chartInstance.current?.destroy();
  }, [
    square,
    rentalRate,
    loanAmount,
    interestRate,
    loanPeriod,
    checkbox,
    keyRate,
  ]);

  return (
    <canvas ref={chartRef} style={{ width: "100%", maxHeight: "400px" }} />
  );
};

export default PaybackChart;
